import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form } from "formik";
import DetailsTitle from "components/common/detailsPage/DetailsTitle";
import InfoItem from "components/common/detailsPage/InfoItem";
import DetailsShimmer from "components/common/shimmer/DetailsShimmer";
import NoDataFound from "components/common/NoDataFound";
import SingleSelect from "components/common/SingleSelect";
import CustomField from "components/form/CustomField";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import { getDeveloperBranchDetails } from "store/slices/developerSettingsSlice";
import { getBranchCameras } from "store/slices/branchCameraSlice";
import {
  addStoreCamera,
  updateStoreCamera,
} from "store/slices/storeCameraSlice";
import { showDeleteModal } from "store/slices/modalSlice";
import { handleSubmitForm } from "helpers/apiUtils";
import { getLocalizedField } from "utils/utils";
import { branchCameraValidationSchema } from "utils/validations";
import { CAMERA_ROLE_TYPE, LANE_OPTIONS } from "utils/constants";
import { Copy } from "lucide-react";
import PageHeader from "components/common/PageHeader";

const CameraRow = ({
  camera,
  branchId,
  dispatch,
  t,
  canEdit,
  canDelete,
  canAdd,
  existingCameras = [],
}) => {
  const isNew = !camera?.cameraId;

  const initialValues = {
    name: camera?.name || "",
    cameraId: camera?.cameraId || "",
    barrierId: camera?.barrierId || "",
    lane: camera?.lane || "",
    role: camera?.role || "",
  };

  const handleSubmit = (values, { resetForm }) => {
    const submitValues = {
      ...values,
      lane: values.role === "ENTRY" ? values.lane : null,
    };
    const payload = isNew
      ? {
          branchId,
          cameras: [
            ...existingCameras.map(
              ({ _id, createdAt, updatedAt, ...rest }) => rest,
            ),
            { ...submitValues, meta: {} },
          ],
        }
      : { branchId, id: camera._id, ...submitValues, meta: {} };

    handleSubmitForm({
      dispatch,
      action: isNew ? addStoreCamera : updateStoreCamera,
      payload,
      onSuccess: () => {
        dispatch(getBranchCameras({ branchId }));
        if (isNew) resetForm();
      },
    });
  };

  if (!isNew && !canEdit && !canDelete) return null;
  if (isNew && !canAdd) return null;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={branchCameraValidationSchema(t)}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, setFieldValue, values }) => (
        <Form className="camera_row border rounded p-3 mb-3">
          <Row>
            <Col lg={4} md={6}>
              <div className="form_field">
                <CustomField
                  label={t("Name")}
                  name="name"
                  placeholder={t("Enter name")}
                  required
                />
              </div>
            </Col>

            <Col lg={4} md={6}>
              <div className="form_field">
                <CustomField
                  label={t("Camera id / ANPR device id")}
                  name="cameraId"
                  placeholder={t("Enter camera id")}
                  required
                />
              </div>
            </Col>

            <Col lg={4} md={6}>
              <div className="form_field">
                <CustomField
                  label={t("Barrier ID")}
                  name="barrierId"
                  placeholder={t("Enter barrier id")}
                  required
                />
              </div>
            </Col>

            <Col lg={6} md={6}>
              <div className="form_field">
                <label>
                  {t("Role")}
                  <span className="text-danger">*</span>
                </label>
                <SingleSelect
                  value={values.role || ""}
                  options={CAMERA_ROLE_TYPE}
                  handleSingleSelectChange={(option) =>
                    setFieldValue("role", option?.value || "")
                  }
                  error={errors.role && touched.role ? errors.role : null}
                />
              </div>
            </Col>
            {values.role === "ENTRY" && (
              <Col lg={6} md={6}>
                <div className="form_field">
                  <label>{t("Lane")}</label>
                  <SingleSelect
                    value={values.lane || ""}
                    options={LANE_OPTIONS}
                    handleSingleSelectChange={(option) =>
                      setFieldValue("lane", option?.value || "")
                    }
                    error={errors.lane && touched.lane ? errors.lane : null}
                  />
                </div>
              </Col>
            )}
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-2">
            {isNew ? (
              <button type="submit" className="main_btn">
                {t("Add Camera")}
              </button>
            ) : (
              <>
                {canDelete && (
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={() =>
                      dispatch(
                        showDeleteModal({
                          title: t("Delete camera"),
                          message: t(
                            "Are you sure you want to delete this camera?",
                          ),
                          itemId: camera._id,
                          actionType: "BRANCH_CAMERA",
                          extra: { branchId },
                        }),
                      )
                    }
                  >
                    {t("Delete")}
                  </button>
                )}
                {canEdit && (
                  <button type="submit" className="main_btn">
                    {t("Update")}
                  </button>
                )}
              </>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

const DeveloperBranchDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const branchId = useQueryParam("id");
  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } =
    canAction("developer_settings");

  const { loading, itemDetails: branchDetails } = useSelector(
    (state) => state.DeveloperSettings,
  );
  const { items: cameras, loading: camerasLoading } = useSelector(
    (state) => state.BranchCamera,
  );
  console.log("Cameras are ", cameras);

  const [copiedField, setCopiedField] = useState("");
  const [showCameraSettings, setShowCameraSettings] = useState(false);

  const copyValue = async (field, value) => {
    if (!value) return;

    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(""), 1500);
  };

  const renderSecretField = (field, value) => (
    <div className="d-flex align-items-center gap-2">
      <input
        type="text"
        className="form-control"
        value={value ? "*".repeat(value.length) : "-"}
        readOnly
      />
      {value && (
        <button
          type="button"
          className="border-0 bg-transparent p-0"
          onClick={() => copyValue(field, value)}
          title={t("Copy")}
        >
          {copiedField === field ? (
            <span className="text-success">{t("Copied")}</span>
          ) : (
            <Copy size={16} />
          )}
        </button>
      )}
    </div>
  );

  useEffect(() => {
    if (branchId && canView) {
      dispatch(getDeveloperBranchDetails(branchId));
      dispatch(getBranchCameras({ branchId }));
    }
  }, [branchId, dispatch, canView]);

  const branchBasicInformationFields = [
    {
      label: t("Store No"),
      value: branchDetails?.storeNo || "-",
      col: { lg: 6 },
    },
    {
      label: t("Name"),
      value: getLocalizedField(branchDetails?.name, language) || "-",
      col: { lg: 6 },
    },
    { isSpacer: true },
    {
      label: t("Ref Id"),
      value: renderSecretField("refId", branchDetails?.refId),
      col: { lg: 6 },
    },
    {
      label: t("API Key"),
      value: renderSecretField("apiKey", branchDetails?.apiKey),
      col: { lg: 6 },
    },
  ];

  const shimmerLayout = [
    {
      col: 12,
      blocks: [
        { rows: 4, cols: 2 },
        { rows: 1, cols: 1 },
      ],
    },
  ];

  if (loading) {
    return (
      <div className="page-content">
        <DetailsTitle title={t("Branch Details")} isBackBtn />
        <DetailsShimmer layout={shimmerLayout} />
      </div>
    );
  }

  return (
    <div className="page-content common_details_page branch_details">
      <PageHeader
        title={t("Branch Details")}
        isBackBtn
        className="details_page_title"
      />
      <Row>
        <Col lg={12} className="">
          <div className="common_card">
            <Row>
              <Col lg={12} className="mb-3">
                <h2 className="title_20">{t("Basic Details")}</h2>
              </Col>

              {branchBasicInformationFields.map((item, index) => {
                if (item.isSpacer) {
                  return <div key={index} className="mt-4 w-100" />;
                }

                return (
                  <InfoItem
                    key={index}
                    label={item.label}
                    value={item.value}
                    col={item.col}
                  >
                    {item.render}
                  </InfoItem>
                );
              })}
            </Row>
          </div>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col lg={12} className="">
          <div className="common_card">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 className="title_20 mb-0">{t("Camera Settings")}</h2>
              {(canView || canAdd) && (
                <button
                  type="button"
                  className="main_btn"
                  onClick={() => setShowCameraSettings((prev) => !prev)}
                >
                  {showCameraSettings
                    ? t("Hide Camera Settings")
                    : t("Camera Settings")}
                </button>
              )}
            </div>

            {showCameraSettings && (
              <>
                {!camerasLoading && cameras?.length === 0 && (
                  <NoDataFound title={t("No cameras found for this branch")} />
                )}

                {cameras?.map((camera) => (
                  <CameraRow
                    key={camera._id}
                    camera={camera}
                    branchId={branchId}
                    dispatch={dispatch}
                    t={t}
                    canEdit={canEdit}
                    canDelete={canDelete}
                  />
                ))}

                {canAdd && (
                  <CameraRow
                    branchId={branchId}
                    dispatch={dispatch}
                    t={t}
                    canAdd={canAdd}
                    existingCameras={cameras}
                  />
                )}
              </>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default DeveloperBranchDetails;
